import React, { useCallback, useEffect, useState } from "react";
import { Pencil, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { Btn } from "./kit";
import { LoginPinButton, PinStatus, useLoginPinRefresh } from "./LoginPin";
import { VendorDialog } from "./VendorDialog";
import { LoginAsButton } from "./PreviewAs";
import { VendorReview } from "./VendorReview";
import { TimesheetReview } from "./TimesheetReview";

const CHIP = { "Can sign in": "bg-emerald-500/15 text-emerald-300", "Needs documents": "bg-amber-500/15 text-amber-300", Inactive: "bg-red-500/15 text-red-300", "No residents assigned": "bg-white/10 text-white/60" };
const phone = (p) => { const d = String(p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : "—"; };

/** Users page · Contract Vendors tab (also used on /admin/contract-vendors for staff with 'Manage contract vendors'). */
export const ContractVendorsTab = () => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(undefined);
  const load = useCallback(() => apiClient.get("/cv/admin/vendors").then(({ data }) => setRows(data.vendors)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  useLoginPinRefresh(load);
  return (
    <div className="space-y-8" data-testid="cv-tab">
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-white/60">Employers who hired TEA residents. They sign in at <b>/vendor/login</b> with their mobile number and PIN.</p>
        <Btn data-testid="cv-invite" onClick={() => setEdit(null)}><UserPlus size={12} /> Invite vendor</Btn>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-white/40 font-mono uppercase text-[10px] tracking-[0.14em]"><tr><th className="p-3 text-left">Vendor</th><th className="p-3 text-left">Login</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Residents</th><th className="p-3 text-left">Documents</th><th className="p-3 text-left">PIN</th><th className="p-3" /></tr></thead>
          <tbody>
            {(rows || []).map((v) => (
              <tr key={v.id} className="border-t border-white/5 align-top" data-testid={`cv-row-${v.id}`}>
                <td className="p-3"><div className="font-medium">{v.name}</div><div className="text-[11px] text-white/50">{v.company}{v.fin_vendor_id ? " · in Finance" : ""}</div></td>
                <td className="p-3 text-xs">{phone(v.phone)}</td>
                <td className="p-3"><span data-testid={`cv-chip-${v.id}`} className={`font-mono text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-sm ${CHIP[v.chip]}`}>{v.chip}</span></td>
                <td className="p-3 text-xs" data-testid={`cv-residents-${v.id}`}>{v.residents.length ? v.residents.map((r) => r.name).join(", ") : <span className="text-white/40">None</span>}</td>
                <td className="p-3 text-xs" data-testid={`cv-docs-${v.id}`}>{v.docs_waiting ? <span className="text-amber-300">{v.docs_waiting} to sign</span> : <span className="text-emerald-300">Complete</span>}</td>
                <td className="p-3 text-[11px]"><PinStatus row={v} testId={`cv-pin-${v.id}`} /></td>
                <td className="p-3 text-right whitespace-nowrap"><LoginPinButton kind="contract_vendor" id={v.id} name={v.name} login={v.phone} source="console_users_vendor" className="mr-2" /><LoginAsButton kind="contract_vendor" id={v.id} name={v.name || v.company} className="mr-2" />
                  <Btn tone="muted" data-testid={`cv-edit-${v.id}`} onClick={() => setEdit(v)}><Pencil size={12} /> Edit</Btn></td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={7} className="p-4 text-white/40" data-testid="cv-empty">No Contract Vendors yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <TimesheetReview />
      <VendorReview />
      {edit !== undefined && <VendorDialog vendor={edit} onClose={() => setEdit(undefined)} onSaved={load} />}
    </div>
  );
};
